import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { HUD } from '../utils/slideThemes';
import { buscarStockArca, importarStockArca } from '../utils/arcaApi';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: HUD.text,
    '& fieldset': { borderColor: HUD.border },
    '&:hover fieldset': { borderColor: 'rgba(56,189,248,0.35)' },
    '&.Mui-focused fieldset': { borderColor: HUD.accent },
  },
  '& .MuiInputLabel-root': { color: HUD.muted },
};

/**
 * Busca Unsplash + importar para o slide ativo.
 */
export default function StockSearchPanel({ onImportAsset, disabled }) {
  const [query, setQuery] = useState('');
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setHint(null);
    try {
      const data = await buscarStockArca(query.trim());
      setFotos(data.fotos || []);
      if (!(data.fotos || []).length) setHint('Nenhuma foto encontrada para este termo.');
    } catch (e) {
      setFotos([]);
      if (e.code === 'UNSPLASH_NOT_CONFIGURED') {
        setHint(e.message);
      } else {
        setError(e.message || 'Erro na busca.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (foto) => {
    setImportingId(foto.id);
    setError(null);
    try {
      const data = await importarStockArca(foto);
      onImportAsset?.(data.asset);
    } catch (e) {
      setError(e.message || 'Erro ao importar foto.');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
        Banco de imagens (Unsplash)
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          label="Buscar"
          placeholder="hospital, equipe, saúde..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          disabled={disabled || loading}
          sx={fieldSx}
        />
        <Button
          variant="outlined"
          onClick={handleSearch}
          disabled={disabled || loading || !query.trim()}
          sx={{ minWidth: 44, borderColor: HUD.borderAccent, color: HUD.accent }}
        >
          {loading ? <CircularProgress size={18} /> : <SearchIcon />}
        </Button>
      </Box>

      {hint && (
        <Alert severity="info" sx={{ py: 0, fontSize: '0.75rem' }}>
          {hint}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ py: 0, fontSize: '0.75rem' }}>
          {error}
        </Alert>
      )}

      {fotos.length > 0 && (
        <ImageList cols={2} gap={6} sx={{ m: 0, maxHeight: 220, overflowY: 'auto' }}>
          {fotos.map((foto) => (
            <ImageListItem key={foto.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <img src={foto.thumb} alt={foto.descricao} loading="lazy" style={{ height: 80, objectFit: 'cover' }} />
              <ImageListItemBar
                title={foto.autor}
                subtitle="Unsplash"
                actionIcon={
                  <IconButton
                    size="small"
                    sx={{ color: '#fff' }}
                    disabled={disabled || importingId === foto.id}
                    onClick={() => handleImport(foto)}
                  >
                    {importingId === foto.id ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AddPhotoAlternateIcon fontSize="small" />
                    )}
                  </IconButton>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      <Typography variant="caption" sx={{ color: HUD.subtle, lineHeight: 1.4 }}>
        Fotos sob Unsplash License. Requer UNSPLASH_ACCESS_KEY no backend.
      </Typography>
    </Box>
  );
}
